import { selMessage } from "@selector/shared";
import { produce } from "immer";
import { uniqBy } from "lodash-es";

import { createErrorHandler } from "@/app-utils/errors.ts";
import type { ISelectOption } from "@/app-utils/form.ts";
import type { IConfiguredAttribute } from "@/app-utils/playbook.ts";
import {
  computeDataGrid,
  computeScalarDefaultValues,
  createAttributeValueInputs,
  extractSampleInput,
  extractSamplesEditingInput,
  extractSamplesValidationInput,
  getAttributeEncodedValue,
  getInitialDataRow,
  getPrimaryParentType,
  getRawAttributeValue,
  getUniqueAttributeId,
  handleInvalidCellWithUniquenessCheckContext,
  type IAttributeValueValidationResponse,
  isAttributeEditable,
  makeAttributesMandatory,
  mandatoryFieldHasNoDefault,
  validateCellValue,
  validateRow,
  validateUniqueColumnsOnClient,
} from "@/app-utils/sample.ts";
import {
  computeGridTemplatedValues,
  computeTemplatesLookup,
  getTemplateDependentAttributes,
  type ISampleTemplateParameter,
  removeAllTemplatePlaceholders,
} from "@/app-utils/sample-template.ts";
import type { TGridSelModelIdentifier } from "@/app-utils/selection-model.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import type { Dictionary, IStringAnyMap, IStringStringMap } from "@/app-utils/type.ts";
import type {
  Attribute,
  AttributeDefaultValue,
  AttributeDefaultValueMapEntry,
  AttributeValue,
  EditSamplesGrid,
  GenomeSearch,
  Group,
  InputEditSamplesInput,
  InputParentInput,
  InputSampleInput,
  InputSamplesValidationInputData,
  InputTableQuerySpec,
  Organism,
  Parent,
  ParentType,
  ProjectFile,
  Region,
  Sample,
  SampleStatus,
  SampleType,
  VocabularyTerm,
} from "@/graphql.ts";
import { getOrderedListOfTrackerAndWorkflowAttributes } from "@/redux/modules/playbook-execution.ts";
import { ATTRIBUTE_ID, ATTRIBUTE_TYPE, COLUMN_SET, COS_ANALYTICAL_ID, LOG_ACTION } from "@/settings/settings.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { notify } from "../../modules/notifications/notify.js";
import {
  RECYCLE_FIELD_NAME,
  RECYCLE_VALUE_FIELD_NAME,
} from "../../ui-modules/playbooks/sample/CombinedRegistrationNgsBasicsScreen.js";
import {
  CLEAR_SAMPLE_EDITING_STATE,
  LOAD_SAMPLE_EDITING_DATA_FAILURE,
  LOAD_SAMPLE_EDITING_DATA_REQUEST,
  LOAD_SAMPLE_EDITING_DATA_SUCCESS,
  REGISTRATION_ADD_SAMPLES,
  REGISTRATION_REMOVE_SAMPLES,
  REMOVE_SAMPLE_GRID_ROW,
  RESET_SAMPLE_GRID,
  SAVE_SAMPLES_REQUEST,
  SAVE_SAMPLES_SUCCESS,
  SEARCH_GENOMES_FOR_SAMPLES_FAILURE,
  SEARCH_GENOMES_FOR_SAMPLES_REQUEST,
  SEARCH_GENOMES_FOR_SAMPLES_SUCCESS,
  SET_SAMPLE_GRID_CHANGES,
  UPDATE_SAMPLE_EDITING_STATE,
  VALIDATE_SAMPLE_GRID_DATA_COMPLETE,
  VALIDATE_SAMPLE_GRID_DATA_REQUEST,
} from "../action-types.js";
import type { IAppState } from "../reducer.js";
import { createReducer, type ReduxAction } from "../redux-utils.js";
import type { IAuditLogPayload } from "./auth.js";
import {
  editingCrossColumnsConversion,
  type IEditable,
  type IEditCellChange,
  type IEditCellMetadata,
  setEditCellData,
  setEditCellValidation,
} from "./data-editing.js";
import { reloadExistingParent } from "./parent.js";
import {
  getTableDataSlice,
  getTableQuerySpecWithSelection,
  loadTableColumns,
  triggerTableRefresh,
} from "./table-data.js";

export type AttributeDefaultValueType = string | number | boolean | VocabularyTerm;
export type ExperimentLevelAttributeValueType =
  | string
  | number
  | boolean
  | moment.Moment
  | Organism
  | Partial<Region>
  | ISelectOption
  | ProjectFile[]; // a form value

export type IAttributeLookup = Dictionary<IConfiguredAttribute>;
export type IAttributeDefaultValueLookup = Dictionary<AttributeDefaultValue>;

// in editing mode the template lookup should take into consideration that the edited group of sample might correspond to multiple sample types.
// since every sample type might have different default values and templates we also -first- map to the sample type identifier.
// in editing mode the template values are used to infer if a cell is read-only.
export type ISampleTypeAttributesTemplatesLookup = Record<number, IAttributesTemplatesLookup>; // map by sampleTypeId

export type IAttributesTemplatesLookup = Dictionary<ISampleTemplateParameter[]>;

// like ITemplateLookup above the default values are up to the sample type however we never use the default values in editing mode (except template as explained above).
export type IDefaultValuesLookup = Dictionary<any>;

export type TSampleCellValue = string | number | boolean | ProjectFile[] | Organism; // | ISelectOption | ProjectFile[];

export interface IAttributeLookupState {
  attributeLookup: IAttributeLookup;
  sampleAttributes: Attribute[]; // attributes for the grid editing logic
  defaultValuesLookup: IDefaultValuesLookup;
  templatesLookup: ISampleTypeAttributesTemplatesLookup;
  editingGrid: IEditable<TSampleCellValue>[]; //length of this array == number of rows == number of samples
}

/**
 * State definition
 */
export interface ISampleEditingState extends IAttributeLookupState {
  isLoading: boolean;
  // for sample editing
  status: SampleStatus;
  sampleEditingTableQuerySpec?: InputTableQuerySpec;
  editedSamples: Sample[];
  // for sample registration
  sampleType: SampleType;
  parentType: ParentType;
  experimentAttributes: Attribute[]; // attributes of the Sample Parent
  experimentValues: Dictionary<ExperimentLevelAttributeValueType>;
  // to distinguish preselected samples from samples that are chosen within a playbook
  usingPreselectedSamples: boolean;
}

const initialState: ISampleEditingState = {
  sampleType: null,
  parentType: null,
  experimentAttributes: [],
  experimentValues: {},
  sampleAttributes: [],
  attributeLookup: {},
  defaultValuesLookup: {},
  isLoading: false,
  editingGrid: [],
  status: null,
  templatesLookup: {},

  sampleEditingTableQuerySpec: null,
  editedSamples: [],
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [UPDATE_SAMPLE_EDITING_STATE]: (
    state: ISampleEditingState,
    payload: Partial<ISampleEditingState>,
  ): ISampleEditingState => {
    return {
      ...state,
      ...payload,
    };
  },
  [LOAD_SAMPLE_EDITING_DATA_REQUEST]: (state: ISampleEditingState): ISampleEditingState => {
    return {
      ...state,
      isLoading: true,
    };
  },

  [LOAD_SAMPLE_EDITING_DATA_SUCCESS]: (
    state: ISampleEditingState,
    payload: Partial<ISampleEditingState>,
  ): ISampleEditingState => {
    const { sampleType, ...rest } = payload;
    const attributeLookup: IAttributeLookup = buildAttributeLookup(payload.sampleAttributes);
    // in registration case, there is only one single sampleType
    const newSampleType = sampleType
      ? {
          ...state.sampleType,
          ...sampleType, // adding the missing sample type properties // WTF ?
        }
      : state.sampleType;

    return {
      ...state,
      ...rest,
      sampleType: newSampleType,
      attributeLookup: attributeLookup,
      isLoading: false,
    };
  },

  [LOAD_SAMPLE_EDITING_DATA_FAILURE]: (state: ISampleEditingState): ISampleEditingState => {
    return {
      ...state,
      isLoading: false,
    };
  },

  [SET_SAMPLE_GRID_CHANGES]: (
    state: ISampleEditingState,
    payload: { changes: IEditCellChange[] },
  ): ISampleEditingState => {
    if (!payload.changes) {
      return state;
    }

    let dataGrid: IEditable[] = state.editingGrid;
    const cellEditingMetadata: Partial<IEditCellMetadata> = { didChange: true };
    payload.changes.forEach((cellChange: IEditCellChange) => {
      dataGrid = setEditCellData(dataGrid, cellChange, cellEditingMetadata);
    });

    return {
      ...state,
      editingGrid: dataGrid,
    };
  },

  [REGISTRATION_ADD_SAMPLES]: (
    state: ISampleEditingState,
    payload: { sampleCount: number; demoSamples: Sample[]; playbookSampleAttributeIds: number[] },
  ): ISampleEditingState => {
    const rows: IEditable[] = [...Array(payload.sampleCount)].map((_, index) =>
      getInitialDataRow(
        state.editingGrid.length + index,
        state.sampleAttributes,
        state.defaultValuesLookup,
        state.sampleType,
        state.templatesLookup,
        payload.demoSamples?.[index],
        payload.playbookSampleAttributeIds,
      ),
    );

    return {
      ...state,
      editingGrid: [...state.editingGrid, ...rows],
    };
  },

  [REGISTRATION_REMOVE_SAMPLES]: (state: ISampleEditingState, count: number): ISampleEditingState => {
    return produce(state, (draft) => {
      draft.editingGrid.splice(-1 * count, count);
    });
  },

  [REMOVE_SAMPLE_GRID_ROW]: (state: ISampleEditingState, rowIndex: number): ISampleEditingState => {
    return produce(state, (draft) => {
      draft.editingGrid.splice(rowIndex, 1);
    });
  },

  [RESET_SAMPLE_GRID]: (state: ISampleEditingState): ISampleEditingState => {
    return {
      ...state,
      editingGrid: [],
      editedSamples: [],
      sampleEditingTableQuerySpec: null,
    };
  },

  [VALIDATE_SAMPLE_GRID_DATA_COMPLETE]: (state: ISampleEditingState, dataRows: IEditable[]): ISampleEditingState => {
    return {
      ...state,
      editingGrid: dataRows,
    };
  },

  [CLEAR_SAMPLE_EDITING_STATE]: (): ISampleEditingState => {
    return { ...initialState };
  },
});

export function buildAttributeLookup(attributes: IConfiguredAttribute[]): IAttributeLookup {
  const attributeLookup: IAttributeLookup = {};
  attributes.forEach(
    (attribute: IConfiguredAttribute) => (attributeLookup[getUniqueAttributeId(attribute)] = attribute),
  );
  return attributeLookup;
}

/**
 * Action creators
 */
export function updateSampleEditingState(state: Partial<ISampleEditingState>) {
  return {
    type: UPDATE_SAMPLE_EDITING_STATE,
    payload: state,
  };
}

export function validateFullSampleGrid() {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient): void => {
    dispatch({
      type: VALIDATE_SAMPLE_GRID_DATA_REQUEST,
    });

    const playbook = getState().playbookExecution.playbook;

    const sampleType: SampleType = getState().sampleEditingState.sampleType;
    const templatesLookup: IAttributesTemplatesLookup = sampleType
      ? getState().sampleEditingState.templatesLookup[sampleType.id]
      : {};
    const attributes: Attribute[] = getState().sampleEditingState.sampleAttributes.filter((a) =>
      isAttributeEditable(a, templatesLookup),
    );

    // In the case of Playbooks we should filter the attributes from the sampleEditingState to get a reasonable subset for the validation
    const defaultValuesLookup = getState().sampleEditingState.defaultValuesLookup;
    const visibleAttributes = playbook
      ? // CAUTION: Although attributes are typed as Attribute[], they are rather IConfiguredAttribute[].
        // In the validators called by validateRow below, the configuredMandatory is considered!
        // Therefore, we are filtering the attributes here in a very basic way, not using any available function.
        attributes.filter(
          (a) =>
            // attribute is in playbook config
            playbook?.config.sampleInput?.map((s) => s?.trackerAttributeId).includes(a.id) ||
            // attribute is potentially not in playbook config, but is mandatory, not automatic and has no defaults
            // (which forces the attribute to be displayed in the grid and we need to validate it)
            (!a.automatic && mandatoryFieldHasNoDefault(a, defaultValuesLookup)),
        ) || []
      : attributes;

    // individual cell checks
    let rowData = getState().sampleEditingState.editingGrid;
    getState().sampleEditingState.editingGrid.forEach((row: IEditable) => {
      rowData = validateRow(rowData, visibleAttributes, row.id);
    });
    // uniqueness-like checks require the column-wide verification
    rowData = validateUniqueColumnsOnClient(rowData, attributes, templatesLookup);

    dispatch({
      type: VALIDATE_SAMPLE_GRID_DATA_COMPLETE,
      payload: rowData,
    });
  };
}

export function validateSampleGridChanges(changes: IEditCellChange[]) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    dispatch({
      type: VALIDATE_SAMPLE_GRID_DATA_REQUEST,
    });

    const sampleType: SampleType = getState().sampleEditingState.sampleType;
    const attributeLookup: IAttributeLookup = getState().sampleEditingState.attributeLookup;
    const templatesLookup: IAttributesTemplatesLookup = sampleType
      ? getState().sampleEditingState.templatesLookup[sampleType.id]
      : {};
    const attributes: Attribute[] = getState().sampleEditingState.sampleAttributes.filter((a) =>
      isAttributeEditable(a, templatesLookup),
    );
    let rowData: IEditable[] = getState().sampleEditingState.editingGrid;

    const attributesToBeChecked: Attribute[] = [];
    changes.forEach((change: IEditCellChange) => {
      const { column, rowId } = change;

      if (!column) {
        // means that all attributeKeys of the row need to be validated again
        rowData = validateRow(rowData, attributes, rowId);
      } else {
        const row: IEditable = rowData.find((row) => row.id == rowId);
        const cellValue: TSampleCellValue = row?.[column.columnId];
        const errorMessage: string = validateCellValue(cellValue, attributeLookup[column.columnId]);
        rowData = setEditCellValidation(rowData, rowId, column.columnId, {
          validating: false,
          validationError: errorMessage,
        });
      }

      // attributeLookup must contain columnKey otherwise we ignore it for now (e.g. analytical attributes during registration could be in theory eligible to changes because of template values)
      if (column && attributeLookup[column.columnId] && !attributesToBeChecked[column.columnId]) {
        // in theory if attributeKey is not defined that should mean that all attributeKeys should be checked
        attributesToBeChecked.push(attributeLookup[column.columnId]);
      }
    });

    rowData = validateUniqueColumnsOnClient(rowData, attributesToBeChecked, templatesLookup);

    dispatch({
      type: VALIDATE_SAMPLE_GRID_DATA_COMPLETE,
      payload: rowData,
    });

    return Promise.resolve();
  };
}

export function fetchSampleTypeAndComputeDefaults(sampleTypeId: number, excludeAnalyticalAttributes = false) {
  return (_dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Partial<ISampleEditingState>> => {
    return Promise.all([
      webApiClient.fetchSampleTypeWizardData(sampleTypeId, excludeAnalyticalAttributes), // response: ISampleType
      webApiClient.fetchSampleAttributeDefaults(sampleTypeId), // response: Array<IAttributeDefaultValueMapEntry>
    ]).then(([sampleType, defaultValues]) => {
      // It would be nice to filter out deselected attributes in the context of a playbook here instead
      // of filtering at all relevant screens. However, this is problematic because the defaults are passed
      // through in the attributes data structure (see e.g. below, computeParentTypeDefaultValues).
      // Once the MT takes care of default values autonomously, we may enhance the logic.

      const defaultValuesLookup: IAttributeDefaultValueLookup = computeDefaultValues(sampleType, defaultValues);
      const templatesLookup: ISampleTypeAttributesTemplatesLookup = computeTemplatesLookup(
        sampleType,
        defaultValuesLookup,
      );
      const primaryParentType: ParentType = getPrimaryParentType(sampleType);
      const parentTypeValues: Dictionary<ExperimentLevelAttributeValueType> = computeParentTypeDefaultValues(
        primaryParentType,
        defaultValuesLookup,
      );
      const sampleAttributes: Attribute[] = sampleType.sampleAttributes
        // for the usage of samples in playbooks (both registration and use existing) we are only
        // interested in annotation attributes
        // [EDIT: WRONG, we also need analytical attributes because they might be built from templates ?? if this is true then much modifications are needed if we remove this filter]
        .filter((attribute) => attribute.columnSet.id !== COS_ANALYTICAL_ID);

      const registrationData: Partial<ISampleEditingState> = {
        sampleType: sampleType,
        defaultValuesLookup: computeScalarDefaultValues(sampleType, defaultValuesLookup),
        templatesLookup: templatesLookup,
        sampleAttributes: sampleAttributes,
        parentType: primaryParentType,
        experimentAttributes: primaryParentType.parentAttributes,
        experimentValues: parentTypeValues,
      };
      return registrationData;
    });
  };
}

function computeParentTypeDefaultValues(
  primaryParentType: ParentType,
  defaultValuesLookup: IAttributeDefaultValueLookup,
): Dictionary<ExperimentLevelAttributeValueType> {
  // set the default form values for the parent type registration form
  const defaultValues: Dictionary<ExperimentLevelAttributeValueType> = {};

  // build the map of default values for  the parent attributes
  primaryParentType.parentAttributes.forEach((parentAttribute: Attribute) => {
    const attributeKey = getUniqueAttributeId(parentAttribute);
    const defaultValue: AttributeDefaultValue = defaultValuesLookup[attributeKey];
    let value: ExperimentLevelAttributeValueType;
    if (defaultValue) {
      if (parentAttribute.attributeType.id === ATTRIBUTE_TYPE.CONTROLLED_TEXT) {
        value = { label: defaultValue.defaultTerm.name, value: defaultValue.defaultTerm.id };
      } else if (parentAttribute.attributeType.id === ATTRIBUTE_TYPE.FREE_NUMERICAL) {
        value = defaultValue.defaultNumber;
      } else if (
        parentAttribute.attributeType.id === ATTRIBUTE_TYPE.FREE_TEXT ||
        parentAttribute.attributeType.id === ATTRIBUTE_TYPE.HYPERLINK
      ) {
        if (defaultValue.template) {
          value = removeAllTemplatePlaceholders(defaultValue.template);
        } else {
          value = defaultValue.defaultText;
        }
      }
      if (value != undefined) {
        defaultValues[attributeKey] = value;
      }
    }
  });

  return defaultValues;
}

export function loadSampleTypeDataForPlaybook(sampleTypeId: number, mandatoryAttributeIds?: number[]) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient): Promise<Partial<ISampleEditingState>> => {
    // make sure loading this sample type is necessary
    const sampleRegistrationPlaybook: ISampleEditingState = getState().sampleEditingState;
    const sampleType: SampleType = sampleRegistrationPlaybook.sampleType;
    // the sampleType has been chosen already during first screen, but we still need the full set of attributes
    if (sampleType && sampleType.id === sampleTypeId && sampleType.sampleAttributes) {
      return Promise.resolve({
        sampleType: sampleType,
        defaultValuesLookup: sampleRegistrationPlaybook.defaultValuesLookup,
        templatesLookup: sampleRegistrationPlaybook.templatesLookup,
        sampleAttributes: sampleRegistrationPlaybook.sampleAttributes,
        parentType: sampleRegistrationPlaybook.parentType,
        experimentAttributes: sampleRegistrationPlaybook.parentType.parentAttributes,
        experimentValues: sampleRegistrationPlaybook.experimentValues,
      } as Partial<ISampleEditingState>);
    }

    dispatch({
      type: LOAD_SAMPLE_EDITING_DATA_REQUEST,
      payload: sampleTypeId,
    });

    return dispatch(fetchSampleTypeAndComputeDefaults(sampleTypeId, false)).then(
      (response: Partial<ISampleEditingState>) => {
        // transform the list of attribute for adding the mandatory attributes in addition
        const sampleAttributes: IConfiguredAttribute[] = makeAttributesMandatory(
          response.sampleAttributes,
          mandatoryAttributeIds,
        );

        dispatch({
          type: LOAD_SAMPLE_EDITING_DATA_SUCCESS,
          payload: {
            ...response,
            sampleAttributes: sampleAttributes,
          },
        });
        return response;
      },
      createErrorHandler(dispatch, {
        actionType: LOAD_SAMPLE_EDITING_DATA_FAILURE,
        actionPayload: sampleTypeId,
        alertTitle: "Fetching of Sample Type and Defaults Have Failed",
        throwError: true,
      }),
    );
  };
}

// case for playbook execution with already registered samples: we want the same columns as for the registration
export function loadSampleDataFromAnnotationColumns(
  sampleTypeId: number,
  tableSpecWithSelection: InputTableQuerySpec,
  mandatoryAttributeIds?: number[], // in case some attributes need to be mandatory in addition (e.g. mapped to mandatory workflow params)
): ReduxAction<Promise<void>> {
  return (dispatch, _getState: () => IAppState, _webApiClient: WebApiClient): Promise<void> => {
    dispatch({
      type: LOAD_SAMPLE_EDITING_DATA_REQUEST,
    });

    return dispatch(fetchSampleTypeAndComputeDefaults(sampleTypeId))
      .then((response: Partial<ISampleEditingState>) => {
        // transform the list of attribute for adding the mandatory attributes in addition
        const sampleAttributes = makeAttributesMandatory(
          response.sampleAttributes.filter((a) => !a.automatic),
          mandatoryAttributeIds,
        );
        dispatch(loadSampleDataFromTableSpec(tableSpecWithSelection, sampleAttributes));
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_SAMPLE_EDITING_DATA_FAILURE,
          actionPayload: sampleTypeId,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

// just loading the table columns definition in order to get our hand on shared things like filters, ...
export function loadSampleDataFromActiveColumns(tableSpec: InputTableQuerySpec) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient) => {
    const state: IAppState = getState();

    const sampleSelectionSpec: InputTableQuerySpec = getTableQuerySpecWithSelection(
      getTableDataSlice(state.tableData, tableSpec),
    );

    dispatch({
      type: LOAD_SAMPLE_EDITING_DATA_REQUEST,
    });

    return webApiClient
      .fetchSampleEditingColumns(sampleSelectionSpec)
      .then((sampleEditingColumns: EditSamplesGrid) => {
        const attributes: Attribute[] = sampleEditingColumns.sampleAttributes.filter((a) => !a.automatic);
        dispatch(loadSampleDataFromTableSpec(tableSpec, attributes));
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_SAMPLE_EDITING_DATA_FAILURE,
          actionPayload: null,
          alertTitle: "Fetching of Sample Editing Columns Failed",
        }),
      );
  };
}

export function saveSamples(tableSpec: InputTableQuerySpec) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient) => {
    const sampleEditingState: ISampleEditingState = getState().sampleEditingState;

    const editingInput: InputEditSamplesInput = extractSamplesEditingInput(sampleEditingState);

    dispatch({
      type: SAVE_SAMPLES_REQUEST,
    });

    dispatch(auditSampleEditingSubmit());
    return webApiClient.saveEditedSamples(editingInput).then((sampleGroup: Group) => {
      dispatch({
        type: SAVE_SAMPLES_SUCCESS,
        payload: {
          sampleGroup,
        },
      });

      // reload the table columns metadata in case the editing has an influence on the available columns e.g. organism association
      // this will as well reload the table rows
      dispatch(loadTableColumns(tableSpec, true));

      // ensure that filtered row count is correct by requesting it again if editing changed the data
      dispatch(triggerTableRefresh(tableSpec));

      dispatch(reloadExistingParent());

      notify.info(
        {
          title: selMessage({
            id: "samples.editing.save.successTitle",
            values: { sampleCount: editingInput.samples.length },
          }),
          children: selMessage({
            id: "samples.editing.save.successMessage",
            values: { sampleCount: editingInput.samples.length },
          }),
        },
        { autoClose: 10000 },
      );
    });
  };
}

// the difference with the above is that the editing data might be about multiple sample types with each their own attributes and templates etc..
export function auditSampleEditingOpen(sampleIds: number[]) {
  return (_dispatch, _getState: () => IAppState, webApiClient: WebApiClient) => {
    // all samples of the current group
    const count: number = sampleIds.length;
    const logMessage: string = "Start editing " + count + " " + pluralizeWord("sample", count);

    const log: IAuditLogPayload = {
      type: LOG_ACTION.MODIFY,
      message: logMessage,
      payload: { ids: sampleIds },
    };
    webApiClient.auditLog(log);
  };
}

// we should consolidate to always consider multiple sample types I guess..
export function auditSampleEditingSubmit() {
  return (_dispatch, getState: () => IAppState, webApiClient: WebApiClient) => {
    const state: IAppState = getState();

    const ids: TGridSelModelIdentifier[] = state.sampleEditingState.editingGrid.map((r: IEditable) => r.id);
    const logMessage: string = "Submit " + ids.length + " changed " + pluralizeWord("sample", ids.length);
    const payload = { ids };

    const log: IAuditLogPayload = {
      type: LOG_ACTION.MODIFY,
      message: logMessage,
      payload: payload,
    };
    webApiClient.auditLog(log);
  };
}

function loadSampleDataFromTableSpec(
  tableSpecWithSelection: InputTableQuerySpec,
  attributes: Attribute[],
): ReduxAction<Promise<Partial<ISampleEditingState>>> {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient) => {
    dispatch({
      type: LOAD_SAMPLE_EDITING_DATA_REQUEST,
    });
    return webApiClient
      .fetchSampleEditingData(
        tableSpecWithSelection,
        attributes.map((a) => a.id),
      )
      .then((sampleEditingData: EditSamplesGrid) => {
        dispatch(auditSampleEditingOpen(sampleEditingData.samples.map((sample: Sample) => sample.id)));
        return sampleEditingData;
      })
      .then((sampleEditingData: EditSamplesGrid): Promise<Partial<ISampleEditingState>> => {
        const sampleTypes: SampleType[] = uniqBy(
          sampleEditingData.samples.map((s) => s.type),
          (sampleType: SampleType) => sampleType.id,
        );

        return Promise.all(
          sampleTypes.map((sampleType: SampleType) => dispatch(fetchSampleTypeAndComputeDefaults(sampleType.id))),
        ).then((allReturnValues: Partial<ISampleEditingState>[]) => {
          // compute the union of templates lookup
          const completeTemplatesLookup: ISampleTypeAttributesTemplatesLookup = {};
          allReturnValues.forEach((returnValue: Partial<ISampleEditingState>) => {
            // here only a subset of the return values are interesting for editing (e.g. sampleAttributes does not need to be modified)
            const { sampleType, templatesLookup } = returnValue;
            completeTemplatesLookup[sampleType.id] = templatesLookup[sampleType.id];
          });

          // take already entered data into account in the new data grid, do not erase user entries
          const existingDataGrid = getState().sampleEditingState.editingGrid;
          const editingData: IEditable[] = computeDataGrid(
            sampleEditingData.samples,
            attributes,
            completeTemplatesLookup,
            existingDataGrid,
          );

          const editingDataPayload: Partial<ISampleEditingState> = {
            editingGrid: editingData,
            templatesLookup: completeTemplatesLookup,
            editedSamples: sampleEditingData.samples,
            sampleAttributes: attributes,
          };

          dispatch({
            type: LOAD_SAMPLE_EDITING_DATA_SUCCESS,
            payload: editingDataPayload,
          });
          dispatch(validateFullSampleGrid());

          return editingDataPayload;
        });
      })

      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_SAMPLE_EDITING_DATA_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function loadSpecificSampleData(tableSpecWithSelection: InputTableQuerySpec, attributeIds: number[] = []) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<EditSamplesGrid> => {
    return webApiClient.fetchSampleEditingData(tableSpecWithSelection, attributeIds).catch(
      createErrorHandler(dispatch, {
        actionType: null,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export function loadSampleTypeForPlaybooksButton(tableSpecWithSelection: InputTableQuerySpec) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<EditSamplesGrid> => {
    return webApiClient.fetchSampleTypeForPlaybooksButton(tableSpecWithSelection).catch(
      createErrorHandler(dispatch, {
        actionType: null,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export function loadSampleInitialBatchData() {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    if (getState().sampleEditingState.editingGrid.length < 1) {
      // if the "Use Demo Data" button was clicked, the number of demo samples defines the number of samples to register
      const useDemoData: boolean = getState().playbookExecution.useDemoData;
      const demoSamples: Sample[] = useDemoData
        ? getState().playbookExecution.playbook.templateEvent?.relatedSamples
        : null;
      const playbookSampleAttributeIds: number[] = getState().playbookExecution.playbook.config.sampleInput?.map(
        (i) => i.trackerAttributeId,
      );
      // init the data table with 10 rows (or the number of demo samples) and all columns
      const sampleCount: number = demoSamples?.length || 10;

      dispatch({
        type: REGISTRATION_ADD_SAMPLES,
        payload: { sampleCount, demoSamples: demoSamples, playbookSampleAttributeIds },
      });
      dispatch(validateFullSampleGrid());
    }
  };
}

export function initParentFromDemoData(parentAttributes: Attribute[], demoParent: Parent) {
  // Called when the "Use Demo Data" button is clicked, applying data derived from the demo template.
  // Note that the redux state was already initialized with defaults, template-derived data etc.!
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    const demoValues: Record<string, any> = {}; // map by key

    parentAttributes
      .filter((pa) => !pa.automatic && pa.modifiable)
      .forEach((pa) => {
        const demoAttributeValue: AttributeValue = demoParent.attributeValues.find((av) => av.attribute.id == pa.id);
        if (demoAttributeValue) {
          const uniqueIdentifier: string = getUniqueAttributeId(pa);
          demoValues[uniqueIdentifier] = getAttributeEncodedValue(
            pa,
            // pass on the complete attribute (including the attribute tpe info) instead of a core attribute with just the ID
            getRawAttributeValue({ ...demoAttributeValue, attribute: pa }),
          );
          if (pa.attributeType.id === ATTRIBUTE_TYPE.BOOLEAN && demoValues[uniqueIdentifier] == null) {
            demoValues[uniqueIdentifier] = false;
          }
        }
      });
    // keep existing values, which were e.g. created based on defaults
    const existingParentValues = getState().sampleEditingState.experimentValues;
    const sampleEditingState: Partial<ISampleEditingState> = {
      // overwrite defaults with demo values where required
      experimentValues: { ...existingParentValues, ...demoValues },
    };

    dispatch(updateSampleEditingState(sampleEditingState));
  };
}

export function setChangesInGrid(changes: IEditCellChange[]) {
  return (dispatch, _getState: () => IAppState, _webApiClient: WebApiClient): Promise<any> => {
    if (changes?.length) {
      return dispatch({
        type: SET_SAMPLE_GRID_CHANGES,
        payload: {
          changes,
        },
      });
    }
  };
}

export function setSampleGridChanges(cellChanges: IEditCellChange[]) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient): Promise<any> => {
    const changes: IEditCellChange[] = cellChanges.map((cellChange) => {
      return { ...cellChange, newValue: editingCrossColumnsConversion(cellChange.column, cellChange.newValue) };
    });
    // a first change in order to update the grid, so that the computing of templates makes use of the fresh values
    dispatch(setChangesInGrid(changes));

    if (getState().sampleEditingState.editedSamples?.length) {
      dispatch(validateSampleGridChanges(changes));
      return Promise.resolve();
    } else {
      return dispatch(computeGridTemplatedValues(changes, getState().sampleEditingState.sampleType)).then(
        (templateChanges: IEditCellChange[]) => {
          if (templateChanges?.length) {
            dispatch(setChangesInGrid(templateChanges));
          }
          return dispatch(validateSampleGridChanges(changes.concat(templateChanges))); // we validate both original changes + template changes in async mode
        },
      );
    }
  };
}

export function checkAttributeUniqueness(attribute: Attribute, values: string[]) {
  return (_dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<string[]> => {
    const samplesValidationInputData: InputSamplesValidationInputData = {
      attributeId: attribute.id,
      values,
    };
    return webApiClient.checkAttributeUniqueness(samplesValidationInputData);
  };
}

export function checkAttributeListUniqueness(attributes: Attribute[], values: IStringAnyMap) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient): Promise<any> => {
    const registrationState = getState().sampleEditingState;
    const templatesLookup: IAttributesTemplatesLookup =
      registrationState.templatesLookup[registrationState.sampleType.id];

    const allValues: IStringStringMap = { ...values };

    const uniquenessCheckPromises = attributes
      .filter((attribute: Attribute) => attribute.valueUniqueConstraint)
      .map((uniqueConstraintAttribute) => {
        const attributeKey: string = getUniqueAttributeId(uniqueConstraintAttribute);
        const valueToValidate: string = allValues[attributeKey];
        return dispatch(checkAttributeUniqueness(uniqueConstraintAttribute, [valueToValidate])).then(
          (responses: string[]) => {
            return responses && responses.length > 0
              ? createUniquenessCheckFormError(uniqueConstraintAttribute, templatesLookup)
              : {};
          },
        );
      });

    return Promise.all(uniquenessCheckPromises).then((vals) => {
      // Promise.all is all or nothing. It resolves once all promises in the array resolve, or reject as soon as one of them rejects. In other words, it either resolves with an array of all resolved values, or rejects with a single error.
      //... since we want to show all the errors are once we need to REJECT the consolidated set of errors
      const consolidatedError: any = vals.reduce((o, x) => ({ ...o, ...x }), {});
      if (Object.keys(consolidatedError).length) {
        return Promise.reject(consolidatedError);
      }
    });
  };
}

// create an object that the redux-form can understand as errors e.g. { attributeKey1: <String>, attributeKey2: <String> }
function createUniquenessCheckFormError(
  attribute: Attribute,
  templatesLookup: IAttributesTemplatesLookup,
): IStringStringMap {
  const errors: IStringStringMap = {};

  // Take care of the checked attribute first
  let message = `The value of ${attribute.attributeName} should be unique but it already exists.`;

  const key: string = getUniqueAttributeId(attribute);
  if (templatesLookup[key]) {
    const dependentAttributes: Attribute[] = getTemplateDependentAttributes(templatesLookup, key);
    message += ` It is computed from ${pluralizeWord("attribute", dependentAttributes.length)} `;
    message += dependentAttributes.map((attr: Attribute) => attr.attributeName).join(", ");
    message += ".";

    dependentAttributes.forEach((depAttribute: Attribute) => {
      errors[getUniqueAttributeId(depAttribute)] =
        `${depAttribute.attributeName} helps building the value of attribute ${attribute.attributeName}.`;
    });
  }
  errors[key] = message;

  return errors;
}

export function uniquenessValidations() {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<any> => {
    dispatch({
      type: VALIDATE_SAMPLE_GRID_DATA_REQUEST,
    });
    const state: IAppState = getState();
    const playbook: ISampleEditingState = state.sampleEditingState;
    const registrationSampleType: SampleType = playbook.sampleType;
    const attributes: Attribute[] = playbook.sampleAttributes;
    const sampleTypeTemplatesLookup: ISampleTypeAttributesTemplatesLookup = playbook.templatesLookup;
    const editedSamples: Sample[] = playbook.editedSamples;
    const editingGrid: IEditable[] = playbook.editingGrid;

    // collect all of the attributes having uniqueness constraints
    const uniqueConstraintAttributes: Attribute[] = attributes
      // also should not be read only
      .filter(
        (attribute: Attribute) =>
          attribute.valueUniqueConstraint === true && !(attribute.automatic || !attribute.editable),
      );

    // trigger a request (with all corresponding values) for each of them, and collect the Promises
    const uniquenessCheckPromises: Promise<IAttributeValueValidationResponse>[] = uniqueConstraintAttributes.map(
      (attribute: Attribute) => {
        const samplesValidationInputData: InputSamplesValidationInputData = extractSamplesValidationInput(
          editingGrid,
          attribute,
          editedSamples,
        );
        return webApiClient
          .checkAttributeUniqueness(samplesValidationInputData)
          .then(
            (response: string[]) =>
              ({ attribute: attribute, duplicates: response }) as IAttributeValueValidationResponse,
          );
      },
    );

    let rowData = editingGrid;
    // when all requests are complete then we check the responses
    return Promise.all(uniquenessCheckPromises).then((responses: IAttributeValueValidationResponse[]) => {
      let uniqunessCheckFailed = false;
      // the responses are checked and any duplicate does lead to an invalid cell
      responses.forEach((validationResponse: IAttributeValueValidationResponse) => {
        if (validationResponse.duplicates && validationResponse.duplicates.length > 0) {
          uniqunessCheckFailed = true;
          const attributeKey: string = getUniqueAttributeId(validationResponse.attribute);
          // all duplicates included in the response should be mapped to a grid cell (to set it to invalid)
          validationResponse.duplicates.forEach((duplicate: string) => {
            const row: IEditable = editingGrid.find(
              (dataRow: IEditable) => dataRow[attributeKey] /*.value*/ === duplicate,
            );
            // the template lookup is up to the sample type
            const sampleTypeId: number = editedSamples?.length
              ? editedSamples.find((sample) => sample.id == row.id)?.type.id
              : registrationSampleType.id;
            const templatesLookup: IAttributesTemplatesLookup = sampleTypeTemplatesLookup[sampleTypeId];

            rowData = handleInvalidCellWithUniquenessCheckContext(
              row.id,
              validationResponse.attribute,
              false,
              templatesLookup || {},
              rowData,
            );
          });
        }
      });

      dispatch({
        type: VALIDATE_SAMPLE_GRID_DATA_COMPLETE,
        payload: rowData,
      });

      // if there is any invalid cell then we reject the promise (so that we do not proceed with the next step)
      if (uniqunessCheckFailed) {
        throw Error("Some value has to be unique but is already used in the database.");
      }
    });
  };
}

export function setSamplesCountForRegistration(newNumber: number) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient): Promise<void> => {
    const editingGrid: IEditable[] = getState().sampleEditingState.editingGrid;
    const currNumber: number = editingGrid.length;

    if (newNumber > currNumber) {
      dispatch({
        type: REGISTRATION_ADD_SAMPLES,
        payload: { sampleCount: newNumber - currNumber },
      });
    } else if (newNumber < currNumber) {
      dispatch({
        type: REGISTRATION_REMOVE_SAMPLES,
        payload: currNumber - newNumber,
      });
    }

    return dispatch(validateFullSampleGrid()); // since the changes are driven by row id, and we don't yet have the row ids at this stage, we validate the full grid
  };
}

export function clearSampleEditingData() {
  return {
    type: CLEAR_SAMPLE_EDITING_STATE,
  };
}

export function searchGenomesForSamples(searchTerm: string, rowIndex: number) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<GenomeSearch> => {
    // find out about genomes currently associated with samples (in the currently open editing grid)
    const editingGrid: IEditable[] = getState().sampleEditingState.editingGrid;
    const assignedOrgIds: number[] = editingGrid
      .filter((r, idx) => idx !== rowIndex && r[ATTRIBUTE_ID.SAMPLE_ORGANISM])
      .map((r) => r[ATTRIBUTE_ID.SAMPLE_ORGANISM].id);

    dispatch({ type: SEARCH_GENOMES_FOR_SAMPLES_REQUEST, payload: searchTerm });

    return webApiClient
      .searchGenomesForSamples(searchTerm)
      .then((response: GenomeSearch) => {
        dispatch({
          type: SEARCH_GENOMES_FOR_SAMPLES_SUCCESS,
          payload: searchTerm,
        });
        // remove the genomes currently selected for other samples
        return { ...response, genomes: response.genomes.filter((g) => !assignedOrgIds.find((a) => a === g.id)) };
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SEARCH_GENOMES_FOR_SAMPLES_FAILURE,
          alertTitle: "Fetching of Genome Options Failed",
          throwError: true,
        }),
      );
  };
}

/******* Utils ***************/

export function getSampleRegistrationInput(sampleEditingState: ISampleEditingState): InputParentInput {
  const editingGrid: IEditable[] = sampleEditingState.editingGrid;

  const sampleRegistrationInput: InputParentInput = {
    parentAttributes: [],
    parentTypeId: sampleEditingState.parentType.id,
    sampleTypeId: sampleEditingState.sampleType.id,
    sampleStatusId: sampleEditingState.status.id,
  };

  const shouldRecycleParent = sampleEditingState.experimentValues[RECYCLE_FIELD_NAME];
  if (shouldRecycleParent) {
    // TODO: why is this guaranteed to be a ISelectOption?
    sampleRegistrationInput.parentId = (
      sampleEditingState.experimentValues[RECYCLE_VALUE_FIELD_NAME] as ISelectOption
    ).value;
  } else {
    const experimentAttributes = sampleEditingState.experimentAttributes
      .filter((a: Attribute) => a.attributeType.id !== ATTRIBUTE_TYPE.CALCULATED) // ignore calculated values
      .filter((a: Attribute) => {
        const attributeKey: string = getUniqueAttributeId(a);
        return (
          sampleEditingState.experimentValues[attributeKey] != null &&
          sampleEditingState.experimentValues[attributeKey].toString().trim().length > 0
        );
      }); // ignore empty/null values;
    sampleRegistrationInput.parentAttributes = createAttributeValueInputs(
      experimentAttributes,
      (attribute) => sampleEditingState.experimentValues[getUniqueAttributeId(attribute)],
    );
  }

  // every grid row will result in a SampleAttributeInput
  let samples: InputSampleInput[] = editingGrid.map((editingRow: IEditable) => {
    return extractSampleInput(editingRow, sampleEditingState, sampleEditingState.editedSamples);
  });

  samples = addDefaultsForAnalyticalAttributes(sampleEditingState, samples);
  sampleRegistrationInput.samples = samples;

  return sampleRegistrationInput;
}

function addDefaultsForAnalyticalAttributes(
  sampleEditingState: ISampleEditingState,
  samples: InputSampleInput[],
): InputSampleInput[] {
  // get all relevant analytical attributes
  const defaultedAnalyticalAttributes: Attribute[] = sampleEditingState.sampleType.sampleAttributes.filter(
    (a) =>
      a.columnSet.id === COLUMN_SET.COS_ANALYTICAL_ID.id &&
      sampleEditingState.defaultValuesLookup[getUniqueAttributeId(a)] != null &&
      !sampleEditingState.templatesLookup[sampleEditingState.sampleType.id][getUniqueAttributeId(a)], // exclude templates otherwise some wrong value is sent to the server
  );

  samples.forEach((s: InputSampleInput) => {
    // do not handle existing samples in editing context (sample ID is given)
    if (!s.sampleId) {
      defaultedAnalyticalAttributes.forEach((a) => {
        const attributeDefault: string = sampleEditingState.defaultValuesLookup[getUniqueAttributeId(a)];
        s.sampleAttributes.push({
          attributeId: a.id,
          attributeTypeId: a.attributeType.id,
          dateValue: null,
          dynamicTerm: null,
          projectFilePath: null,
          textValue: a.attributeType.id === ATTRIBUTE_TYPE.FREE_TEXT ? attributeDefault : null,
          numberValue: a.attributeType.id === ATTRIBUTE_TYPE.FREE_NUMERICAL ? attributeDefault : null,
          vocabularyTermId:
            a.attributeType.id === ATTRIBUTE_TYPE.CONTROLLED_TEXT
              ? a.vocabularyTerms.find((v) => v.name === attributeDefault).id
              : null,
        });
      });
    }
  });

  return samples;
}

export function findAttributeByKey(
  attributeKey: string,
  sampleType: SampleType,
  primaryParentType: ParentType,
): Attribute {
  return (
    sampleType.sampleAttributes.find((a: Attribute) => getUniqueAttributeId(a) == attributeKey) || // either sample attr ...
    primaryParentType?.parentAttributes.find((a: Attribute) => getUniqueAttributeId(a) == attributeKey) // ... or primary parent attr
  );
}

function computeDefaultValues(
  sampleType: SampleType,
  defaultValues: AttributeDefaultValueMapEntry[],
): IAttributeDefaultValueLookup {
  const primaryParentType: ParentType = getPrimaryParentType(sampleType);
  const defaultValuesLookup: IAttributeDefaultValueLookup = {};
  defaultValues.map((attributeDefault: AttributeDefaultValueMapEntry) => {
    const attribute: Attribute = findAttributeByKey(attributeDefault.attributeId, sampleType, primaryParentType);

    if (attribute) {
      defaultValuesLookup[getUniqueAttributeId(attribute)] = attributeDefault.data;
    }
  });
  return defaultValuesLookup;
}

export function resetSampleGrid() {
  return {
    type: RESET_SAMPLE_GRID,
  };
}
